"use client";

import { mapToSupportedLocale, useLocale } from "@i18n/react";
import { useMemo } from "react";
import { useMediaQuery } from "usehooks-ts";

import { useRequestGeolocation } from "../data/requests/geolocation/useRequestGeolocation";
import { useRequestMe } from "../data/requests/me/useRequestMe";
import { useRequestSubscriptions } from "../data/requests/subscription/useRequestSubscriptions";
import { hasExpired } from "../lib/utils/subscription";
import { getDeviceIsMobile } from "../utils/device/getDeviceIsMobile";
import { useAccessibility } from "./useAccessibility";

/**
 * Hook to collect device and user properties for analytics and feature flags.
 * Returns a consistent set of properties that can be used across different providers.
 */
export const useDeviceAndUserProperties = () => {
  const locale = useLocale();
  const { preferredTheme, hlwFontStyle, hlwLetterSpacing, hlwWordSpacing } =
    useAccessibility();
  const prefersDarkMode =
    useMediaQuery("(prefers-color-scheme: dark)") ?? false;
  const prefersReducedMotion =
    useMediaQuery("(prefers-reduced-motion: reduce)") ?? false;
  const prefersMoreContrast =
    useMediaQuery("(prefers-contrast: more)") ?? false;
  const { query: queryMe } = useRequestMe();
  const { query: querySubscriptions } = useRequestSubscriptions();
  const { query: queryGeolocation } = useRequestGeolocation();

  // Prioritize user language, then browser, then fallback
  const userLocale = useMemo(() => {
    return (
      queryMe.data?.language ||
      (typeof window !== "undefined"
        ? mapToSupportedLocale(navigator.language)
        : locale)
    );
  }, [queryMe.data?.language, locale]);

  const deviceAndUserProperties = useMemo(() => {
    const isWindowExists = typeof window !== "undefined";
    const user = queryMe.data;
    const subscriptions = querySubscriptions.data;
    const geolocation = queryGeolocation.data;

    // Determine current theme (resolved from "match" preference)
    const currentTheme =
      preferredTheme === "match"
        ? prefersDarkMode
          ? "dark"
          : "light"
        : preferredTheme;

    const props: Record<string, string> = {
      screenWidth: isWindowExists ? String(window.innerWidth) : "0",
      screenHeight: isWindowExists ? String(window.innerHeight) : "0",
      platform: "web",
      deviceType: getDeviceIsMobile() ? "mobile" : "desktop",
      darkMode: String(prefersDarkMode ?? false),
      theme: currentTheme,
      locale: userLocale,
      isHighContrastEnabled: String(prefersMoreContrast ?? false),
      isReducedMotionEnabled: String(prefersReducedMotion ?? false),
      fontStyle: hlwFontStyle,
      letterSpacing: hlwLetterSpacing,
      wordSpacing: hlwWordSpacing,
    };

    // Add locale country code if available
    if (geolocation?.country) {
      props.localeCountryCode = geolocation.regionCode;
      props.ipCountry = geolocation.country;
    }

    // Add user properties if authenticated
    if (user) {
      props.hasEmail = String(!!user.email);
      props.hasPhone = String(!!user.phone);
      if (user.name) {
        props.name = user.name;
        const nameParts = user.name.split(" ");
        props.firstName = nameParts[0] || "";
        props.lastName = nameParts.slice(1).join(" ") || user.last_name || "";
      }
      props.isAuthenticated = String(!!user.id);
      if (user.email) {
        props.email = user.email;
      }
      if (user.notifications_enabled !== undefined) {
        props.notificationsEnabled = String(user.notifications_enabled);
      }
      if (user.daily_quote_notifications !== undefined) {
        props.dailyQuoteNotifications = String(user.daily_quote_notifications);
      }
      if (user.consent) {
        props.consent = JSON.stringify(user.consent);
      }
      if (user.created_at) {
        props.dateSignedUp = new Date(user.created_at).toISOString();
      }
      if (subscriptions?.[0]?.plan?.external_id) {
        props.subscriptionPlan = subscriptions[0].plan.external_id;
      }
      if (subscriptions?.[0]) {
        props.hasExpiredSubscription = String(hasExpired(subscriptions[0]));
      }
    }

    return props;
  }, [
    userLocale,
    preferredTheme,
    prefersDarkMode,
    prefersReducedMotion,
    prefersMoreContrast,
    hlwFontStyle,
    hlwLetterSpacing,
    hlwWordSpacing,
    queryMe.data,
    querySubscriptions.data,
    queryGeolocation.data,
  ]);

  return deviceAndUserProperties;
};
